import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Image,
  Text,
  VStack,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
  IconButton,
  Tooltip,
  FormErrorMessage,
  List,
  ListItem,
  Spinner,
  useToast
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon, CopyIcon, CheckIcon } from "@chakra-ui/icons";
import { useDispatch } from "react-redux";
import { registerUser } from "../../../Redux/Login/action";
import StateSelector from "../../Shared/StateSelector";
import ShapeCaptcha from "../../Shared/ShapeCaptcha";
import apiClient from "../../../utils/apiConfig";

const Signup = ({ isOpen, onOpen, onClose, cancelRef, handelhideshow }) => {
  console.time("signup_modal_load");
  const [formData, setFormData] = useState({
    burrowerId: '',
    burrowerId2: '',
    password: '',
    password2: '',
    email: '',
    orderNumber: '',
    friendPass: '',
    userState: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recoveryPin, setRecoveryPin] = useState(null);
  const [pinCopied, setPinCopied] = useState(false);
  
  // --- Referral / Friend Pass lookup state ---
  const [friendPassSuggestions, setFriendPassSuggestions] = useState([]);
  const [friendPassMatched, setFriendPassMatched] = useState(false);
  const [friendPassLoading, setFriendPassLoading] = useState(false);
  const [showFriendPassDropdown, setShowFriendPassDropdown] = useState(false);
  const friendPassTimerRef = useRef(null);
  const friendPassWrapperRef = useRef(null);

  // --- Shape CAPTCHA state ---
  const [captchaVerified, setCaptchaVerified] = useState(false);
  
  const dispatch = useDispatch();
  const toast = useToast();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (friendPassWrapperRef.current && !friendPassWrapperRef.current.contains(e.target)) {
        setShowFriendPassDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced friend pass lookup
  const lookupFriendPass = useCallback((query) => {
    if (friendPassTimerRef.current) clearTimeout(friendPassTimerRef.current);

    if (!query || query.length < 2) {
      setFriendPassSuggestions([]);
      setFriendPassMatched(false);
      setShowFriendPassDropdown(false);
      return;
    }

    friendPassTimerRef.current = setTimeout(async () => {
      setFriendPassLoading(true);
      try {
        const res = await apiClient.get(`/api/auth/lookup-user?q=${encodeURIComponent(query)}`);
        if (res.data?.success && res.data.matches) {
          setFriendPassSuggestions(res.data.matches);
          setShowFriendPassDropdown(res.data.matches.length > 0);
          // Exact case-insensitive match?
          const exactMatch = res.data.matches.some(
            m => m.toLowerCase() === query.toLowerCase()
          );
          setFriendPassMatched(exactMatch);
        }
      } catch (err) {
        console.error('Friend pass lookup error:', err);
      } finally {
        setFriendPassLoading(false);
      }
    }, 350);
  }, []);
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.burrowerId) {
      newErrors.burrowerId = 'Burrower ID is required';
    } else if (formData.burrowerId.length < 4 || formData.burrowerId.length > 10) {
      newErrors.burrowerId = 'Burrower ID must be 4-10 characters';
    } else if (!/[0-9!@#$%^&*]/.test(formData.burrowerId)) {
      newErrors.burrowerId = 'Burrower ID must contain at least one number or special character';
    }
    
    if (!formData.burrowerId2) {
      newErrors.burrowerId2 = 'Please confirm your Burrower ID';
    } else if (formData.burrowerId !== formData.burrowerId2) {
      newErrors.burrowerId2 = 'Burrower IDs do not match';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!formData.password2) {
      newErrors.password2 = 'Please confirm your password';
    } else if (formData.password !== formData.password2) {
      newErrors.password2 = 'Passwords do not match';
    }

    if (!captchaVerified) {
      newErrors.captcha = 'Please complete the bot check';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when the user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Trigger referral lookup when friend pass changes
    if (name === 'friendPass') {
      lookupFriendPass(value);
    }
  };

  const handleSelectFriendPass = (username) => {
    setFormData(prev => ({ ...prev, friendPass: username }));
    setFriendPassMatched(true);
    setShowFriendPassDropdown(false);
    setFriendPassSuggestions([]);
  };
  
  const handleShowPasswordClick = () => setShowPassword(!showPassword);
  const handleShowConfirmPasswordClick = () => setShowConfirmPassword(!showConfirmPassword);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll modal body to the top so the user sees which field has an error
      const modalBody = document.querySelector('.chakra-modal__body');
      if (modalBody) modalBody.scrollTop = 0;
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log("Submitting registration form", formData);
      
      const result = await dispatch(registerUser({
        burrowerId: formData.burrowerId,
        password: formData.password,
        email: formData.email || undefined,
        orderNumber: formData.orderNumber,
        friendPass: formData.friendPass,
        userState: formData.userState || undefined
      }));
      
      setIsSubmitting(false);
      
      if (result.success) {
        console.log("Registration successful");
        
        // Show the one-time Recovery PIN instead of closing immediately
        if (result.recoveryPin) {
          setRecoveryPin(result.recoveryPin);
        } else {
          // Fallback if no PIN returned (shouldn't happen)
          toast({
            title: 'Account created.',
            description: "You can now sign in with your Burrower ID",
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          onClose();
          handelhideshow();
        }
      } else {
        console.error("Registration error:", result.error);
        // Show specific error messages from the server
        toast({
          title: 'Registration failed',
          description: result.message || 'An error occurred during registration',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        
        // Set field-specific errors if returned from server
        if (result.fieldErrors) {
          setErrors(prev => ({ ...prev, ...result.fieldErrors }));
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      setIsSubmitting(false);
      toast({
        title: 'Registration failed',
        description: error.response?.data?.message || 'An error occurred during registration',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  // Log when modal is fully rendered
  React.useEffect(() => {
    if (isOpen) {
      console.timeEnd("signup_modal_load");
    }
  }, [isOpen]);

  const handleCopyPin = () => {
    if (recoveryPin) {
      navigator.clipboard.writeText(recoveryPin).then(() => {
        setPinCopied(true);
        setTimeout(() => setPinCopied(false), 2000);
      });
    }
  };

  const handlePinDismiss = () => {
    setRecoveryPin(null);
    setPinCopied(false);
    setFormData({
      burrowerId: '',
      burrowerId2: '',
      password: '',
      password2: '',
      email: '',
      orderNumber: '',
      friendPass: '',
      userState: ''
    });
    // Don't close the modal — switch to the Sign In view so the user
    // sees the login form immediately instead of being dumped on the home screen.
    handelhideshow();
  };

  return (
    <Modal isOpen={isOpen} onClose={recoveryPin ? undefined : onClose} closeOnOverlayClick={!recoveryPin}>
      <ModalOverlay />
      <ModalContent bg="white" color="black">
        {recoveryPin ? (
          <>
            <ModalHeader textAlign="center">
              <Text fontSize="xl" color="green.500">Account Created!</Text>
            </ModalHeader>
            <ModalBody>
              <VStack spacing={4}>
                <Alert status="warning" borderRadius="md" flexDirection="column" textAlign="center" py={4}>
                  <AlertIcon boxSize="24px" mb={2} />
                  <AlertTitle fontSize="md">Save Your Recovery PIN</AlertTitle>
                  <AlertDescription fontSize="sm">
                    This PIN is shown <strong>only once</strong>. Write it down or copy it now.
                    You will need it to recover your account if you forget your password.
                  </AlertDescription>
                </Alert>
                
                <Box 
                  bg="gray.800" 
                  borderRadius="lg" 
                  p={6} 
                  textAlign="center"
                  border="2px solid"
                  borderColor="orange.400"
                  w="100%"
                >
                  <Text fontSize="xs" color="gray.400" mb={2}>YOUR RECOVERY PIN</Text>
                  <Code 
                    fontSize="2xl" 
                    fontWeight="bold" 
                    bg="transparent" 
                    color="orange.300"
                    letterSpacing="wider"
                  >
                    {recoveryPin}
                  </Code>
                  <Tooltip label={pinCopied ? "Copied!" : "Copy PIN"}>
                    <IconButton
                      icon={pinCopied ? <CheckIcon /> : <CopyIcon />}
                      size="sm"
                      ml={3}
                      colorScheme={pinCopied ? "green" : "gray"}
                      onClick={handleCopyPin}
                      aria-label="Copy Recovery PIN"
                    />
                  </Tooltip>
                </Box>

                <Text fontSize="xs" color="gray.500" textAlign="center">
                  If you lose this PIN and have no email on file, there is no way to recover your account.
                </Text>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button 
                colorScheme="green" 
                w="100%" 
                onClick={handlePinDismiss}
              >
                I've Saved My PIN — Continue to Sign In
              </Button>
            </ModalFooter>
          </>
        ) : (
        <>
        <ModalHeader textAlign="center">
          <Image
            src="https://bunny-burrow-pull-1.b-cdn.net/Logo-Longlow-White-Back.jpg"
            width="600px"
            maxW="100%"
            mx="auto"
            mb={4}
          />
          <Text>Create a Burrower Account</Text>
          <Text fontSize="sm" color="gray.500">
            Get access to Burrow TV, Tools and Community
          </Text>
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} as="form" onSubmit={handleSubmit}>
            <FormControl isInvalid={!!errors.burrowerId}>
              <FormLabel>Burrower ID</FormLabel>
              <Input
                name="burrowerId"
                value={formData.burrowerId}
                onChange={handleChange}
                placeholder="Enter your Burrower ID"
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                Your Burrower ID must be 4-10 characters long and include at least one number
              </Text>
              <FormErrorMessage>{errors.burrowerId}</FormErrorMessage>
            </FormControl>
            
            <FormControl isInvalid={!!errors.burrowerId2}>
              <FormLabel>Confirm Burrower ID</FormLabel>
              <Input
                name="burrowerId2"
                value={formData.burrowerId2}
                onChange={handleChange}
                placeholder="Confirm your Burrower ID"
              />
              <FormErrorMessage>{errors.burrowerId2}</FormErrorMessage>
            </FormControl>
            
            <FormControl isInvalid={!!errors.password}>
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={handleShowPasswordClick}>
                    {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>
            
            <FormControl isInvalid={!!errors.password2}>
              <FormLabel>Confirm Password</FormLabel>
              <InputGroup>
                <Input
                  name="password2"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.password2}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={handleShowConfirmPasswordClick}>
                    {showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.password2}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel>Email (Optional)</FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email for password recovery"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Optional — used only for account recovery if you lose your Recovery PIN
              </Text>
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>
            
            <FormControl>
              <FormLabel>Order Number (Optional)</FormLabel>
              <Input
                name="orderNumber"
                value={formData.orderNumber}
                onChange={handleChange}
                placeholder="Enter order number if available"
              />
            </FormControl>
            
            <FormControl ref={friendPassWrapperRef} position="relative">
              <FormLabel>Friend Pass (Optional)</FormLabel>
              <InputGroup>
                <Input
                  name="friendPass"
                  value={formData.friendPass}
                  onChange={handleChange}
                  onFocus={() => {
                    if (friendPassSuggestions.length > 0) setShowFriendPassDropdown(true);
                  }}
                  placeholder="Enter a Burrower ID who referred you"
                  borderColor={friendPassMatched ? 'green.400' : undefined}
                  _focus={friendPassMatched ? { borderColor: 'green.400', boxShadow: '0 0 0 1px #48BB78' } : undefined}
                />
                {friendPassLoading && (
                  <InputRightElement>
                    <Spinner size="xs" color="gray.400" />
                  </InputRightElement>
                )}
                {friendPassMatched && !friendPassLoading && (
                  <InputRightElement>
                    <CheckIcon color="green.400" />
                  </InputRightElement>
                )}
              </InputGroup>
              {showFriendPassDropdown && friendPassSuggestions.length > 0 && (
                <List
                  position="absolute"
                  zIndex={10}
                  bg="gray.700"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.600"
                  mt={1}
                  w="100%"
                  maxH="140px"
                  overflowY="auto"
                  boxShadow="lg"
                >
                  {friendPassSuggestions.map((name) => (
                    <ListItem
                      key={name}
                      px={3}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: 'gray.600' }}
                      onClick={() => handleSelectFriendPass(name)}
                      fontSize="sm"
                    >
                      {name}
                    </ListItem>
                  ))}
                </List>
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                Type a Burrower ID to search — green means matched!
              </Text>
            </FormControl>

            <StateSelector
              value={formData.userState}
              onChange={handleChange}
            />

            <ShapeCaptcha
              onVerified={(v) => {
                setCaptchaVerified(v);
                if (errors.captcha && v) {
                  setErrors(prev => ({ ...prev, captcha: '' }));
                }
              }}
              isInvalid={!!errors.captcha}
            />
          </VStack>
        </ModalBody>

        <ModalFooter flexDirection="column" alignItems="center" gap={4}>
          <Button
            colorScheme="blue"
            w="100%"
            onClick={handleSubmit}
            isLoading={isSubmitting}
          >
            Create Account
          </Button>

          <Text textAlign="center">
            Already have an account?{' '}
            <Text
              as="span"
              color="blue.500"
              cursor="pointer"
              onClick={() => {
                console.log("Auth modal transition:", { from: "signup", to: "signin" });
                // Don't close the modal, just switch to signin
                handelhideshow();
              }}
            >
              Sign In
            </Text>
          </Text>
        </ModalFooter>
        </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default Signup;
